/**
 * useSchool — shared hook for school-branding hydration.
 *
 * Goals:
 *  1. Eliminate the flash-of-fallback-color when navigating between
 *     school-branded pages by hydrating from a module-level cache and
 *     localStorage on first paint.
 *  2. DRY the duplicate `GET /schools/{id}` fetch + brandColor fallback
 *     pattern across the assessment / teacher / parent / child pages.
 *
 * Behaviour:
 *  - Returns `null` if no schoolId is provided.
 *  - On first call for a given schoolId, hydrates synchronously from
 *    in-memory cache or localStorage so the very first render already
 *    has brand colors.
 *  - Always issues a background `GET /schools/{id}` to refresh — updates
 *    propagate within one page navigation.
 *  - Brand color callers should use BRAND_COLOR_FALLBACK (a neutral slate)
 *    rather than indigo so cold-cache loads don't flash a "wrong" color.
 */
import { useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { setLastSchoolSlug } from '@/lib/schoolSession';

export const BRAND_COLOR_FALLBACK = '#64748B'; // slate-500 — brand-neutral

const memCache = new Map(); // schoolId -> schoolDoc
const LS_PREFIX = 'yukti_school_';

function readLs(schoolId) {
  try {
    const raw = localStorage.getItem(LS_PREFIX + schoolId);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeLs(schoolId, doc) {
  try {
    localStorage.setItem(LS_PREFIX + schoolId, JSON.stringify(doc));
  } catch {
    /* quota / disabled — non-fatal */
  }
}

function readCache(schoolId) {
  if (!schoolId) return null;
  const mem = memCache.get(schoolId);
  if (mem) {
    if (mem.slug) setLastSchoolSlug(mem.slug);
    return mem;
  }
  const ls = readLs(schoolId);
  if (ls) {
    memCache.set(schoolId, ls);
    if (ls.slug) setLastSchoolSlug(ls.slug);
  }
  return ls;
}

export default function useSchool(schoolId) {
  // Synchronous initial value — instant first paint when cached
  const [school, setSchool] = useState(() => readCache(schoolId));
  const lastFetchedRef = useRef(null);

  useEffect(() => {
    if (!schoolId) {
      setSchool(null);
      return undefined;
    }
    // Hydrate instantly when navigating into a different school's page
    const cached = readCache(schoolId);
    if (cached && cached !== school) setSchool(cached);

    // Skip the network call if we just refreshed for this schoolId
    if (lastFetchedRef.current === schoolId) return undefined;
    lastFetchedRef.current = schoolId;

    let cancelled = false;
    api.get(`/schools/${schoolId}`)
      .then((res) => {
        if (cancelled) return;
        memCache.set(schoolId, res.data);
        writeLs(schoolId, res.data);
        if (res.data?.slug) setLastSchoolSlug(res.data.slug);
        setSchool(res.data);
      })
      .catch(() => {
        // Keep whatever cached value we already have.
      });
    return () => { cancelled = true; };
    // We intentionally exclude `school` from deps to avoid refetch loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId]);

  return school;
}
